import { useState } from "react";
import { Phone, PhoneOff, Mic, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCallMonitor } from "@/hooks/useCallMonitor";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface CallMonitorWidgetProps {
  jobId?: string;
  leadId?: string;
  mode?: 'job' | 'lead' | 'new-lead';
}

export function CallMonitorWidget({ jobId, leadId, mode = 'new-lead' }: CallMonitorWidgetProps) {
  const { toast } = useToast();
  const {
    isActive,
    transcript,
    lastCallResults,
    startMonitoring,
    stopMonitoring,
  } = useCallMonitor();

  const [isProcessing, setIsProcessing] = useState(false);

  const handleStart = async () => {
    try {
      await startMonitoring();
      toast({
        title: "Call started",
        description: "Bridgette is listening",
      });
    } catch (error) {
      console.error('Failed to start monitoring:', error);
      toast({
        title: "Failed to start call",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleStop = async () => {
    setIsProcessing(true);
    try {
      await stopMonitoring({ jobId, leadId, mode });
      toast({
        title: "Call ended",
        description: `Created ${lastCallResults?.notes || 0} notes and ${lastCallResults?.tasks || 0} tasks`,
      });
    } catch (error) {
      console.error('Failed to stop monitoring:', error);
      toast({
        title: "Error ending call",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className={cn(
      "transition-all duration-200",
      isActive && "border-destructive/50 bg-destructive/5"
    )}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Call with Bridgette
          </div>
          {isActive && (
            <Badge variant="destructive" className="gap-1">
              <Mic className="w-3 h-3" />
              Live
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Quick Stats */}
        {!isActive && lastCallResults && (
          <div className="flex items-center justify-around py-2 px-3 rounded-md bg-muted/50 text-sm">
            <div className="text-center">
              <div className="font-bold text-primary">{lastCallResults.notes}</div>
              <div className="text-xs text-muted-foreground">Notes</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-primary">{lastCallResults.tasks}</div>
              <div className="text-xs text-muted-foreground">Tasks</div>
            </div>
          </div>
        )}

        {/* Active Call Info */}
        {isActive && transcript.length > 0 && (
          <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-muted/50 text-sm">
            <MessageSquare className="w-4 h-4 text-primary" />
            <span className="text-muted-foreground">
              {transcript.length} message{transcript.length !== 1 ? 's' : ''} recorded
            </span>
          </div>
        )}

        {/* Action Button */}
        {!isActive ? (
          <Button
            onClick={handleStart}
            className="w-full"
            size="sm"
          >
            <Phone className="w-4 h-4 mr-2" />
            Start Call
          </Button>
        ) : (
          <Button
            onClick={handleStop}
            variant="destructive"
            className="w-full"
            size="sm"
            disabled={isProcessing}
          >
            <PhoneOff className="w-4 h-4 mr-2" />
            {isProcessing ? "Ending..." : "End Call"}
          </Button>
        )}

        {/* Hint text */}
        {!isActive && transcript.length === 0 && (
          <p className="text-xs text-muted-foreground text-center">
            Bridgette will listen and take notes automatically
          </p>
        )}
      </CardContent>
    </Card>
  );
}
